import { ITelephonyConfiguration, IUser, UserViewModel } from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import { parse } from '@AppModels/QueryStringUtils';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { toast } from 'sonner';
import { getDisplayName } from '../../../../models/NameUtils';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { Button } from '../../../../web/components-v2/Button';
import { UserAutocomplete } from '../../../../web/components/UserAutocomplete';
import PlaceholderImageSource from '../../../assets/call-from-unknown-placeholder.svg';
import { useResponseHandler } from '../../../hooks/error';
import { BasicPage } from '../../BasicPage';
import { NumbersList } from './components/NumbersList';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
}

export const PhoneNumbers: React.FC<IProps> = observer(({ className = '' }) => {
	const userSession = useUserSession();
	const history = useHistory();
	const location = useLocation();

	const [selectedUser, setSelectedUser] = useState<IUser>(null);
	const [selectedUserId, setSelectedUserId] = useState(null);
	const [userLoadError] = useResponseHandler('LoadUser', 'Unable to load user');
	const [phoneNumbers, setPhoneNumbers] = useState<ITelephonyConfiguration[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const updatedSearch = parse(location.search) as { userId?: string };
		if (updatedSearch.userId) {
			setSelectedUserId(updatedSearch.userId);
		}
	}, [location.search]);

	useEffect(() => {
		// A newer pick can land before an older load; the cleanup marks that load stale so it can't overwrite the pick.
		let isStale = false;
		loadNumbersForUser(() => isStale);
		if ((!selectedUser || selectedUser?.id !== selectedUserId) && selectedUserId) {
			const user = new UserViewModel(userSession, { id: selectedUserId });
			user
				.load()
				?.then(() => {
					if (isStale) {
						return;
					}
					setSelectedUser(user.toJs());
					history.push(`/settings/phoneNumbers?userId=${selectedUserId}`);
				})
				?.catch(userLoadError);
		}
		return () => {
			isStale = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedUserId]);

	const loadNumbersForUser = async (isStale: () => boolean) => {
		if (!selectedUserId) {
			setLoading(false);
			return;
		}

		setLoading(true);
		setPhoneNumbers([]);
		const result = await userSession.webServiceHelper.callWebServiceAsync<ITelephonyConfiguration[]>(
			`aida/userPhoneNumbers/${selectedUserId}`,
			'GET'
		);
		if (isStale()) {
			return;
		}
		if (!result) {
			toast.error(`Could not load phone numbers for ${getDisplayName(selectedUser)}`);
			setLoading(false);
			return;
		}

		setPhoneNumbers(result.value ?? []);
		setLoading(false);
	};

	const syncNumbersForUser = async () => {
		if (!selectedUserId) {
			return;
		}

		setLoading(true);
		setPhoneNumbers([]);
		const result = await userSession.webServiceHelper.callWebServiceAsync<ITelephonyConfiguration[]>(
			`twilio/requestNumbers?userId=${selectedUserId}`,
			'POST'
		);
		if (!result) {
			toast.error(`Could not load phone numbers for ${getDisplayName(selectedUser)}`);
			setLoading(false);
			return;
		}

		setPhoneNumbers(result.value ?? []);
		setLoading(false);
	};

	const onUserSelected = (user: IUser) => {
		if (!user?.id) {
			setSelectedUserId(null);

			setSelectedUser(null);

			setPhoneNumbers(null);
			history.push(`/settings/phoneNumbers`);
			return;
		}

		setSelectedUser(user);
		setSelectedUserId(user.id);
		history.push(`/settings/phoneNumbers?userId=${user.id}`);
	};

	const isEmpty = !phoneNumbers?.length || phoneNumbers?.length === 0;

	return (
		<BasicPage appearance='light' className={className} title='Phone Numbers'>
			<div className={css(styleSheet.container)}>
				<div className={css(styleSheet.searchBar)}>
					<UserAutocomplete
						ariaLabel='Search users'
						onChange={onUserSelected}
						placeholder='Search for a user to see associated phone numbers'
						value={selectedUser}
					/>
				</div>

				{selectedUser && !loading && (
					<div className={css(styleSheet.actionsBar, isEmpty ? styleSheet.actionsBarCenter : null)}>
						<Button variant='secondary' onClick={syncNumbersForUser}>
							{`Refill numbers for ${getDisplayName(selectedUser)}`}
						</Button>
					</div>
				)}

				{isEmpty && !loading ? (
					<div className={css(styleSheet.placeholder)}>
						<div className={css(styleSheet.placeholderCircle)}>
							<img alt='' className={css(styleSheet.placeholderImage)} src={PlaceholderImageSource} />
						</div>
					</div>
				) : (
					<NumbersList isLoading={loading} onClickToAddNumbers={syncNumbersForUser} phoneNumbersList={phoneNumbers} />
				)}
			</div>
		</BasicPage>
	);
});
